/**
 * Compass evidence accordion — the anti-hallucination surface shared by the
 * support panel (CompassImpactPanel) and CR Analysis (CrAnalysisPage).
 *
 * Entries carry a `category` (Compass, 2026-08-02) and are grouped by it in a
 * fixed display order, keeping the original lookup order within each group
 * because that reflects the investigation sequence. Analyses stored before
 * the field existed have no categories at all — those render as a flat list
 * rather than one pointless "Other" group.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CompassEvidence } from '../../services/compassApi';

/** Fixed display order; anything unrecognised or missing falls to "Other". */
const CATEGORY_ORDER = [
  'Graph search',
  'Table',
  'Field',
  'Procedure',
  'Method',
  'Module',
  'UI screen',
  'Other',
] as const;

function groupByCategory(evidence: CompassEvidence[]): [string, CompassEvidence[]][] {
  const buckets = new Map<string, CompassEvidence[]>();
  for (const ev of evidence) {
    const raw = ev.category?.trim();
    const key = raw && (CATEGORY_ORDER as readonly string[]).includes(raw) ? raw : 'Other';
    const list = buckets.get(key);
    if (list) list.push(ev);
    else buckets.set(key, [ev]);
  }
  // Fixed order, empty groups skipped.
  return CATEGORY_ORDER.filter((c) => buckets.has(c)).map((c) => [c, buckets.get(c)!]);
}

function Entry({ ev }: { ev: CompassEvidence }) {
  const input = JSON.stringify(ev.input);
  return (
    <li className="truncate" title={input}>
      {ev.tool}({input})
    </li>
  );
}

interface Props {
  evidence: CompassEvidence[];
  /** Border colour of the section divider, to match the host panel. */
  borderClass?: string;
}

export function CompassEvidenceAccordion({ evidence, borderClass = 'border-purple-200' }: Props) {
  const [open, setOpen] = useState(false);
  if (evidence.length === 0) return null;

  const grouped = groupByCategory(evidence);
  const categorised = evidence.some((ev) => ev.category?.trim());

  return (
    <div className={`border-t ${borderClass} pt-2`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[10px] font-medium text-purple-700"
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        Evidence ({evidence.length} graph lookup{evidence.length === 1 ? '' : 's'})
      </button>
      {open &&
        (categorised ? (
          <div className="mt-1 space-y-1.5">
            {grouped.map(([category, entries]) => (
              <div key={category}>
                <div className="text-[10px] font-semibold text-purple-600">
                  {category} ({entries.length})
                </div>
                <ul className="mt-0.5 space-y-0.5 font-mono text-[10px] text-purple-800 pl-2">
                  {entries.map((ev, i) => (
                    <Entry key={`${category}-${i}`} ev={ev} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          // Pre-category analyses: flat list, exactly as before.
          <ul className="mt-1 space-y-0.5 font-mono text-[10px] text-purple-800">
            {evidence.map((ev, i) => (
              <Entry key={i} ev={ev} />
            ))}
          </ul>
        ))}
    </div>
  );
}
